var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};

import logger from '../util/logger';
import Call from './Call';
import { checkSubscribeResponse } from './helpers';
import { Result } from '../messages/Verto';
import { SwEvent } from '../util/constants';
import { VertoMethod, NOTIFICATION_TYPE } from './constants';
import { trigger, deRegister } from '../services/Handler';
import { State, ConferenceAction } from './constants';
import { MCULayoutEventHandler } from './LayoutHandler';

class VertoHandler {
  constructor(session) {
    this.session = session;
  }

  _ack(id, method) {
    const msg = new Result(id, method);

    if (this.nodeId) {
      msg.targetNodeId = this.nodeId;
    }

    this.session.execute(msg);
  }

  handleMessage(msg) {
    const {
      session
    } = this;
    const {
      id,
      method,
      params
    } = msg;
    const {
      callID,
      eventChannel,
      eventType
    } = params;
    const attach = method === VertoMethod.Attach;

    if (eventType === 'channelPvtData') {
      return this._handlePvtEvent(params.pvtData);
    }

    if (callID && session.calls.hasOwnProperty(callID)) {
      if (attach) {
        session.calls[callID].hangup({}, false);
      } else {
        session.calls[callID].handleMessage(msg);

        this._ack(id, method);

        return;
      }
    }

    const _buildCall = () => {
      const call = new Call(session, {
        id: callID,
        remoteSdp: params.sdp,
        destinationNumber: params.callee_id_number,
        remoteCallerName: params.caller_id_name,
        remoteCallerNumber: params.caller_id_number,
        callerName: params.callee_id_name,
        callerNumber: params.callee_id_number,
        attach
      });
      call.nodeId = this.nodeId;
      return call;
    };

    switch (method) {
      case VertoMethod.Punt:
        session.disconnect();
        break;

      case VertoMethod.Invite:
        {
          const call = _buildCall();

          call.setState(State.Ringing);

          this._ack(id, method);

          break;
        }

      case VertoMethod.Attach:
        {
          const call = _buildCall();

          if (this.session.autoRecoverCalls) {
            call.answer();
          } else {
            call.setState(State.Recovering);
          }

          call.handleMessage(msg);
          break;
        }

      case VertoMethod.Event:
      case 'webrtc.event':
        if (!eventChannel) {
          logger.error('Verto received an unknown event:', params);
          return;
        }

        const protocol = session.relayProtocol;
        const firstValue = eventChannel.split('.')[0];

        if (session._existsSubscription(protocol, eventChannel)) {
          trigger(protocol, params, eventChannel);
        } else if (eventChannel === session.sessionid) {
          this._handleSessionEvent(params.eventData);
        } else if (session._existsSubscription(protocol, firstValue)) {
          trigger(protocol, params, firstValue);
        } else if (session.calls.hasOwnProperty(eventChannel)) {
          session.calls[eventChannel].handleMessage(msg);
        } else {
          trigger(SwEvent.Notification, params, session.uuid);
        }

        break;

      case VertoMethod.Info:
        params.type = NOTIFICATION_TYPE.generic;
        trigger(SwEvent.Notification, params, session.uuid);
        break;

      case VertoMethod.ClientReady:
        params.type = NOTIFICATION_TYPE.vertoClientReady;
        trigger(SwEvent.Notification, params, session.uuid);
        break;

      default:
        logger.warn('Verto message unknown method:', msg);
    }
  }

  _retrieveCallId(packet, laChannel) {
    const callIds = Object.keys(this.session.calls);

    if (packet.action === 'bootObj') {
      const me = packet.data.find(pr => callIds.includes(pr[0]));

      if (me instanceof Array) {
        return me[0];
      }
    } else {
      return callIds.find(id => this.session.calls[id].channels.includes(laChannel));
    }
  }

  _handlePvtEvent(pvtData) {
    return __awaiter(this, void 0, void 0, function* () {
      const {
        session
      } = this;
      const protocol = session.relayProtocol;
      const {
        action,
        laChannel,
        laName,
        chatChannel,
        infoChannel,
        modChannel,
        conferenceMemberID,
        role,
        callID
      } = pvtData;

      switch (action) {
        case 'conference-liveArray-join':
          {
            const _liveArrayBootstrap = () => {
              session.vertoBroadcast({
                nodeId: this.nodeId,
                channel: laChannel,
                data: {
                  liveArray: {
                    command: 'bootstrap',
                    context: laChannel,
                    name: laName
                  }
                }
              });
            };

            const tmp = {
              nodeId: this.nodeId,
              channels: [laChannel],
              handler: _ref => {
                let {
                  data: packet
                } = _ref;

                const id = callID || this._retrieveCallId(packet, laChannel);

                if (id && session.calls.hasOwnProperty(id)) {
                  const call = session.calls[id];

                  call._addChannel(laChannel);

                  call.extension = laName;
                  call.handleConferenceUpdate(packet, pvtData).then(error => {
                    if (error === 'INVALID_PACKET') {
                      _liveArrayBootstrap();
                    }
                  });
                }
              }
            };
            const result = yield session.vertoSubscribe(tmp).catch(error => {
              logger.error('liveArray subscription error:', error);
            });

            if (checkSubscribeResponse(result, laChannel)) {
              _liveArrayBootstrap();
            }

            break;
          }

        case 'conference-liveArray-part':
          {
            let call = null;

            if (laChannel && session._existsSubscription(protocol, laChannel)) {
              const {
                callId = null
              } = session.subscriptions[protocol][laChannel];
              call = session.calls[callId] || null;

              if (callId !== null) {
                const notification = {
                  type: NOTIFICATION_TYPE.conferenceUpdate,
                  action: ConferenceAction.Leave,
                  conferenceName: laName,
                  participantId: Number(conferenceMemberID),
                  role
                };

                if (!trigger(SwEvent.Notification, notification, callId, false)) {
                  trigger(SwEvent.Notification, notification, session.uuid);
                }

                if (call === null) {
                  deRegister(SwEvent.Notification, null, callId);
                }
              }
            }

            const channels = [laChannel, chatChannel, infoChannel, modChannel];
            session.vertoUnsubscribe({
              nodeId: this.nodeId,
              channels
            }).then(_ref2 => {
              let {
                unsubscribedChannels = []
              } = _ref2;

              if (call) {
                call.channels = call.channels.filter(c => !unsubscribedChannels.includes(c));
              }
            }).catch(error => {
              logger.error('liveArray unsubscribe error:', error);
            });
            break;
          }
      }
    });
  }

  _handleSessionEvent(eventData) {
    switch (eventData.contentType) {
      case 'layout-info':
      case 'layer-info':
        MCULayoutEventHandler(this.session, eventData);
        break;

      case 'logo-info':
        {
          const notification = {
            type: NOTIFICATION_TYPE.conferenceUpdate,
            action: ConferenceAction.LogoInfo,
            logo: eventData.logoURL
          };
          trigger(SwEvent.Notification, notification, this.session.uuid);
          break;
        }
    }
  }

}

export default VertoHandler;